import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setOktaSessionSnapshot } from "./oktaSessionStore.js";
import {
  clearStoredSswsSession,
  readStoredSswsSession,
  writeStoredOrgOnly,
  writeStoredSswsSession,
} from "./oktaSessionStorage.js";
import { OKTA_SSWS_UNAUTHORIZED_EVENT } from "./oktaSswsUnauthorizedDispatch.js";

const OktaSessionContext = createContext(null);

function readInitialPair() {
  const s = readStoredSswsSession();
  return { orgUrl: s?.orgUrl ?? null, apiToken: s?.apiToken ?? null };
}

/**
 * SSWS tenant: org URL + API token in React state, mirrored to sessionStorage for tab reloads.
 */
export function OktaSessionProvider({ children }) {
  const initial = readInitialPair();
  const [orgUrl, setOrgUrl] = useState(initial.orgUrl);
  const [apiToken, setApiToken] = useState(initial.apiToken);
  const [sswsUnauthorized, setSswsUnauthorized] = useState(false);

  useEffect(() => {
    setOktaSessionSnapshot({ orgUrl, apiToken });
  }, [orgUrl, apiToken]);

  const setSession = useCallback(({ orgUrl: nextOrg, apiToken: nextToken }) => {
    const o = nextOrg != null ? String(nextOrg).trim() || null : null;
    const t = nextToken != null ? String(nextToken).trim() || null : null;
    setOrgUrl(o);
    setApiToken(t);
    setSswsUnauthorized(false);
    if (o && t) writeStoredSswsSession(o, t);
    else if (o && !t) writeStoredOrgOnly(o);
    else clearStoredSswsSession();
  }, []);

  const clearSession = useCallback(() => {
    setOrgUrl(null);
    setApiToken(null);
    setOktaSessionSnapshot({ orgUrl: null, apiToken: null });
    clearStoredSswsSession();
    setSswsUnauthorized(false);
  }, []);

  /** Okta rejected the SSWS token — keep org URL, clear token + storage, show reconnect UI. */
  const stripTokenAfterUnauthorized = useCallback(() => {
    setSswsUnauthorized(true);
    setApiToken(null);
    setOrgUrl((o) => {
      setOktaSessionSnapshot({ orgUrl: o, apiToken: null });
      if (o) writeStoredOrgOnly(o);
      else clearStoredSswsSession();
      return o;
    });
  }, []);

  const clearSswsUnauthorized = useCallback(() => {
    setSswsUnauthorized(false);
  }, []);

  useEffect(() => {
    const onUnauth = () => stripTokenAfterUnauthorized();
    window.addEventListener(OKTA_SSWS_UNAUTHORIZED_EVENT, onUnauth);
    return () => window.removeEventListener(OKTA_SSWS_UNAUTHORIZED_EVENT, onUnauth);
  }, [stripTokenAfterUnauthorized]);

  const value = useMemo(
    () => ({
      orgUrl,
      apiToken,
      oktaConnected: Boolean(orgUrl && apiToken),
      sswsUnauthorized,
      setSession,
      clearSession,
      stripTokenAfterUnauthorized,
      clearSswsUnauthorized,
    }),
    [orgUrl, apiToken, sswsUnauthorized, setSession, clearSession, stripTokenAfterUnauthorized, clearSswsUnauthorized],
  );

  return <OktaSessionContext.Provider value={value}>{children}</OktaSessionContext.Provider>;
}

export function useOktaSession() {
  const ctx = useContext(OktaSessionContext);
  if (!ctx) throw new Error("useOktaSession must be used within OktaSessionProvider");
  return ctx;
}
